
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalItemDetails, FetchList, GetConsumed, GetNotConsumed, PatchConsumeLater, PostConsumeLater } from "./ApiRoutes";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MainLoader, { SecondaryLoader } from "@/utils/Loaders";
import { CalendarClockIcon, ChartLineIcon, Check, ChevronDown, ChevronUp, Clock, Dot, ExternalLink, Ghost, ListOrderedIcon, Pencil, Star } from "lucide-react";
import { matchListTag, mediaTypeIcon, truncateText } from "@/utils/TextUtils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { toast, Toaster } from "sonner";
import { UserContext } from "@/App";
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"



type ItemType = "shows" | "movies" | "songs" | "artists" | "albums";

interface ListItem {
    id: string,
    external_id:string,
    title:string,
    image_url:string,
    year:string,
    order:Number,
    artist?:string,
    comment?:string,
    status?:boolean,
}

export default function ListViewer() {
    const [listID, setListID] = useState<string | null>(null);
    const {user, setUser} = useContext(UserContext)

    const username:string = user.username || localStorage.getItem("username");

    useEffect(() => {
            const pathSegments = location.pathname.split('/');
            const editSegmentIndex = pathSegments.indexOf("view");
            
            if(pathSegments[editSegmentIndex + 1]){
                setListID(pathSegments[editSegmentIndex + 1]);
                console.log("List ID:", listID);
            }
            else{
                history.go(-1);
            }
    
        },[location.pathname])


    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ['list', listID],
        queryFn: () => FetchList(listID ?? ""),
        enabled: !!listID,
    })



    
    if (isPending) {
        return <MainLoader />;
    }
    if(isError){
        return(
            <div className="flex flex-col h-2/3 gap-2 justify-center items-center text-center text-white mt-15 font-semibold">
                <h3 className="text-xl sm:text-2xl"> Something went wrong loading this list :/</h3>
                <p className="text-neutral-600 font-medium">We may be expierencing issues, try again later</p>
                <Ghost className="mt-5 w-10 h-10 text-neutral-700" />
            </div>
        )
    }
     if(data && isSuccess){
        console.log(listID)
        console.log(username);
        if(data.list_type == "songs" || data.list_type == "albums"){
            return(
            <main className="flex flex-col h-full items-center">
                <Toaster className="z-30" />
                <section className="relative flex flex-col max-w-full lg:min-w-300 justify-center pb-10">
                    <header className="sticky w-full top-0 rounded-md z-30 px-10 backdrop-blur-xs flex flex-col text-white mt-10 py-5 font-semibold text-center">
                        <div className="flex absolute inset-0 bg-gradient-to-t from-neutral-950/20 via-neutral-950 to-neutral-950 z-10" />    
                        <div className="z-20">
                            <Link className="hover:underline mb-0" to={`/profile/${data.owner}`}>{data.owner}</Link>
                            <h3 className="text-xl sm:text-2xl flex justify-center items-center gap-2">
                                {data.title} 
                                { String(username) == data.owner && <Link to={`/list/edit/${listID}`}><Pencil className="h-5 w-5 hover:cursor-pointer"/></Link>}
                            </h3>
                            <p className="text-neutral-400 font-medium">{data.description}</p>
                            <section className="flex flex-wrap justify-center items-center gap-2 mt-2">
                                {mediaTypeIcon(data.list_type) ? mediaTypeIcon(data.list_type) : null}
                                {data.is_ranked && <ListOrderedIcon/>}
                                {matchListTag(data.tag) ? matchListTag(data.tag) : null}
                                
                            </section>
                        </div>
                    
                        
                    </header>
                    <section className="w-full max-w-600 lg:w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 justify-items-center items-center mx-auto sm:px-10 md:px-15">
                    {
                        data.items.map((item: ListItem) => (
                        <ItemDetails id={item.external_id} itemType={data.list_type}>
                        <article key={item.id} className="relative flex flex-col items-center text-white md:h-85 w-80 my-4 sm:my-4 lg:my-6">
                                <div className="relative flex justify-start h-55 md:h-65 lg:h-75 z-10 hover:cursor-pointer duration-300 ease-in-out hover:scale-110 hover:rotate-5">
                                    <img draggable={false} className="relative w-auto h-full rounded-lg" src={item.image_url} />
                                    <div className="flex absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-transparent z-10" />    
                                    {data.is_ranked && PositionStyle(Number(item.order) + 1) }
                                </div>
                                <div className="flex flex-col px-5 justify-center items-center text-center rounded-b-lg">
                                    <h5 className="flex justify-center items-center text-neutral-500 text-xs font-semibold mt-2 w-full">{item.artist}</h5>
                                    <h5 className="flex justify-center items-center text-md font-semibold w-full">{item.title}</h5>
                                    <p className="text-neutral-400 text-xs break-keep">{item.comment}</p>
                                </div>
                                
                        </article>
                        </ItemDetails>
                        ))
                    }
                    </section>
                </section>
            </main>
            )
        }
        return(
            <main className="flex flex-col h-full items-center">
                <Toaster className="z-30" />
                <section className="relative flex flex-col max-w-full lg:min-w-300 justify-center pb-10">
                    <header className="sticky w-full top-0 rounded-md z-30 px-10 backdrop-blur-xs flex flex-col text-white mt-10 py-5 font-semibold text-center">
                        <div className="flex absolute inset-0 bg-gradient-to-t from-neutral-950/20 via-neutral-950 to-neutral-950 z-10" />    
                        <div className="z-20">
                            <Link className="hover:underline mb-0" to={`/profile/${data.owner}`}>{data.owner}</Link>
                            <h3 className="text-xl sm:text-2xl flex justify-center items-center gap-2">
                                {data.title} 
                                { String(username) == data.owner && <Link to={`/list/edit/${listID}`}><Pencil className="h-5 w-5 hover:cursor-pointer"/></Link>}
                            </h3>
                            <p className="text-neutral-400 font-medium">{data.description}</p>
                            <section className="flex flex-wrap justify-center items-center gap-2 mt-2">
                                {mediaTypeIcon(data.list_type) ? mediaTypeIcon(data.list_type) : null}
                                {data.is_ranked && <ListOrderedIcon/>}
                                {matchListTag(data.tag) ? matchListTag(data.tag) : null}
                                
                            </section>
                        </div>
                    
                        
                    </header>
                    <section className="w-full max-w-600 lg:w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 justify-items-center items-center mx-auto sm:px-10 md:px-15">
                    {
                        data.items.map((item: ListItem) => (
                        <ItemDetails id={item.external_id} itemType={data.list_type}>
                        <article key={item.id} className="relative flex flex-col items-center text-white md:h-85 w-80 my-4 sm:my-4 lg:my-6">
                                <div className="relative flex justify-start h-55 md:h-65 lg:h-75 z-10 hover:cursor-pointer duration-300 ease-in-out hover:scale-110 hover:rotate-5">
                                    <img draggable={false} className="relative w-auto h-full rounded-lg " src={item.image_url} />
                                    <div className="flex absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-transparent z-10" />    
                                    {data.is_ranked && PositionStyle(Number(item.order) + 1)}
                                </div>
                                <div className="flex flex-col px-5 justify-center items-center text-center rounded-b-lg">
                                    <h5 className="flex justify-center items-center text-md font-semibold mt-2 w-full">{item.title}</h5>
                                    <p className="text-neutral-400 text-xs break-keep">{item.comment}</p>
                                </div>
                                
                        </article>
                        </ItemDetails>
                        ))
                    }
                    </section>
                </section>
            </main>
            )
     }
}


function PositionStyle(position:number) {
    switch (position) {
        case 1:
            return <h4 className={`absolute text-yellow-500 z-30 -left-3 -top-7 text-5xl font-semibold text-shadow-black`}>{position}</h4>
        case 2:
            return <h4 className={`absolute text-slate-400 z-30 -left-3 -top-7 text-5xl font-semibold text-shadow-black`}>{position}</h4>
        case 3:
            return <h4 className={`absolute text-orange-500 z-30 -left-3 -top-7 text-5xl font-semibold text-shadow-black`}>{position}</h4>
        default:
            return <h4 className={`absolute text-white z-30 -left-3 -top-7 text-5xl font-semibold text-shadow-black`}>{position}</h4>
    }
}

interface itemDetailsProps {
    id:string;
    itemType:"shows"|"movies"|"songs"|"artists"|"albums";
    children: ReactNode;
}

export function ItemDetails({id, itemType, children}:itemDetailsProps) {
    
    const [viewOverview, setViewOverview] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<String>("overview"); // lower case 
    const [activeVideo, setActiveVideo] = useState<String>();
    const {user, setUser} = useContext(UserContext);
    const username:string =  user?.username ?? localStorage.getItem("username");
    const queryClient = useQueryClient();

    const { mutate:consumeLaterMutate, isPending:isConsumeLaterPending } = useMutation({
        mutationFn: ({ username, data }: PostConsumeLaterParams) => PostConsumeLater(username, data),

        onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['consumed', username] });
        queryClient.invalidateQueries({ queryKey: ['consumed', username, "profile"] });
        queryClient.invalidateQueries({ queryKey: ['consumed-infinite', username] });
        toast.success("Consume later updated");
        },

        onError: (error) => {
        toast.error("Failed to update: "+ error);
        },
    });

    const { mutate:consumedMutate, isPending:isConsumedMutatePending } = useMutation({
        mutationFn: ({ data }: PostConsumeLaterParams) => PostConsumeLater(username,data),

        onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['consumed', username] });
        queryClient.invalidateQueries({ queryKey: ['consumed-infinite', username] });
        toast.success("Consume later updated");
        },

        onError: (error) => {
        toast.error("Failed to update: "+ error);
        },
    });

    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ['item', id],
        queryFn: () => ExternalItemDetails(id, itemType),
        enabled: isOpen,
    })

    const { isPending:isFetchConsumePending, isError:isFetchConsumeError, data:fetchConsumeData, 
        error:fetchConsumeError, isSuccess:isFetchConsumeSuccess } = useQuery({
        queryKey: ['consumed', username, "not consumed"], // match third key to active tab values in consumelater.tsx
        queryFn: () => GetNotConsumed(username),
        enabled: !!username,
    })

     const { isPending:isConsumedPending, isError:isConsumedError, data:isConsumedData, 
        error:consumedError, isSuccess:isConsumedSuccess } = useQuery({
        queryKey: ['consumed', username, "consumed"],
            

         // match third key to active tab values in consumelater.tsx
        queryFn: () => GetConsumed(username),
        enabled: !!username,
    })
    
    // we use a set to filter through a potentially limitless amount of items in consume later
    // as it searches for results in fixed O(1) (a better method is redis but expensive to implement)
    const consumeLaterIdSet = useMemo(() => {
    if (!fetchConsumeData?.results) return new Set();

    return new Set(fetchConsumeData.results.map((result:object) => result.item.external_id));
    }, [fetchConsumeData]); 

    const prevConsumedIdSet = useMemo(() => {
        if (!isConsumedData?.results) return new Set();
        return new Set(isConsumedData.results.map((result:object) => result.item.external_id));
    }, [isConsumedData]); 
    

    useEffect(()=>{
        !isOpen ? setActiveVideo("") : null; 
    },[isOpen])


    let consumeLaterData:object;
    switch (itemType) {
        case "movies":
            consumeLaterData = {
                title:(data?.title ?? data?.name),
                external_id:id,
                image_url:"https://image.tmdb.org/t/p/w500/"+data?.poster_path,
                year:String(data?.release_date).split("-")[0],
                item_type:itemType,
                status:false,
            }
            break;
        case "shows":
            consumeLaterData = {
                title:(data?.title ?? data?.name),
                external_id:id,
                image_url:"https://image.tmdb.org/t/p/w500/"+data?.poster_path,
                year:String(data?.release_date).split("-")[0],
                item_type:itemType,
                status:false,
            }
            break;
        case "songs":
            consumeLaterData = {
                title:(data?.name),
                external_id:id,
                image_url:data?.album?.images[0].url,
                year:String(data?.release_date).split("-")[0],
                item_type:itemType,
                artist:data?.artists[0]?.name,
                status:false,
            }
            break;
        case "albums":
            consumeLaterData = {
                title:(data?.name),
                external_id:id,
                image_url:data?.images[0].url,
                year:String(data?.release_date).split("-")[0],
                item_type:itemType,
                artist:data?.artists[0]?.name,
                status:false,
            }
            break;
        case "artists":
            consumeLaterData = {
                title:(data?.name),
                external_id:id,
                image_url:data?.images[0].url,
                item_type:itemType,
                artist:data?.name,
                status:false,
            }
            break;
        default:
            break;
    }
    


    const handleConsumeLaterClick = (status=false) => {
        if(status){
            consumeLaterData.status = true;
            consumedMutate({ username:String(username), data:consumeLaterData });
        }
        else{
            consumeLaterMutate({ username:String(username), data:consumeLaterData });
        }
        
    }


    if(itemType == "movies" || itemType == "shows"){
        return(
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="bg-black/60 backdrop-blur-md dark border-0 text-white max-h-full min-h-50 ease-in-out duration-200">
                <ScrollArea className="flex max-h-svh pb-6 sm:pb-0">
                 
                <DialogHeader className="pb-2">
                <DialogTitle>{isSuccess ? data.tagline ?? data.name : "Details"}</DialogTitle>
                <DialogDescription>
                    
                </DialogDescription>
                </DialogHeader>
               {isPending && <div className="flex items-center justify-center"><SecondaryLoader /></div>}
                    {isError && <p>Error loading details.</p>}
                    {isSuccess && data && (
                        <section className="flex relative flex-col w-full h-full overflow-hidden min-h-100">
                          <div className={`flex relative w-full ease-in-out duration-200 ${activeVideo != "" ? "h-45 sm:h-55 md:h-70" : "h-45"} justify-center items-center overflow-hidden`}>
                            { activeVideo == "" && /* no active video selected */
                            <>
                                <div
                                className="absolute inset-0 z-10 rounded-lg scale-100 bg-size-[100%]"
                                style={data.backdrop_path ? {  backgroundImage: `url(${"https://image.tmdb.org/t/p/original/"+data.backdrop_path})` } : {backgroundImage: "radial-gradient(circle,rgba(31, 38, 61, 1) 0%, rgba(10, 10, 28, 1) 48%, rgba(0, 0, 0, 1) 100%)"}}
                                />
                                <Skeleton className="absolute inset-0 z-0 rounded-lg scale-100 bg-size-[100%]" />

                                <div className="flex absolute inset-0 bg-gradient-to-tr from-black/80 via-black/20 to-transparent z-10" />
                                <div className="flex absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" />
                            </>
                            }
                            { activeVideo != "" && /* active video selected */
                            <>
                                <iframe src={`https://www.youtube-nocookie.com/embed/${activeVideo}?autoplay=1&modestbranding=1`} width="100%" height="100%" title={data.name ?? data.title} frameborder="0" allowfullscreen="1"></iframe>
                              
                            </>
                            }
                          
                          </div>
                            <article className={`relative ease-in-out duration-300 flex flex-col ${activeVideo == "" ? " pl-30 sm:pl-35" : "pt-2"}`}>
                                <h3 className={`font-semibold sm:text-lg ${activeVideo == "" ? "" : ""}`}>{data.title ?? data.name}</h3>
                                { activeVideo == "" && <img className="flex absolute -top-25 left-0 sm:left-5 rounded-lg z-20 h-40" src={"https://image.tmdb.org/t/p/w500/"+data.poster_path} /> }
                                <div className="flex items-center text-sm gap-2 font-semibold"> 
                                    <span className="flex items-center text-sm gap-1">
                                        <Star className="-rotate-90" /> {Math.round((data.vote_average + Number.EPSILON) * 100) / 100} 
                                    </span>
                                    {data.status == "Canceled" && <Badge className="bg-red-900 text-white">{data.status}</Badge>}
                                    {data.status == "Returning Series" && <Badge className="bg-green-900 text-white">{data.status}</Badge>}
                                    {data.status == "Ended" && <Badge className="bg-orange-800 text-white">{data.status}</Badge>}
                                    {data.status == "In Production" && <Badge className="bg-purple-800 text-white">{data.status}</Badge>}
                                </div>
                            </article>
                            { activeVideo == "" &&
                            <article className="pt-5 text-xs text-neutral-500">
                                {
                                    viewOverview == false ? 
                                    <div className="flex flex-col items-center relative hover:cursor-pointer " onClick={() => setViewOverview(!viewOverview)}>{truncateText(data.overview, 100) }
                                         <ChevronDown className="absolute bottom-0 z-20 w-5 h-5" />
                                    </div>
                                    :
                                    <p className="flex relative hover:cursor-pointer ease-in-out duration-150 " onClick={() => setViewOverview(!viewOverview)}>{data.overview} <ChevronUp className="absolute -bottom-4 left-1/2 z-20 w-5 h-5" /></p>

                                }
                                
                            </article>
                            }

                            <article className="flex mt-5 gap-5 justify-center">
                                <div className="flex flex-col items-center">
                                    <h4 className="font-semibold text-neutral-500 text-xs">Initial release</h4>
                                    <p className="text-sm text-neutral-400 ">
                                        {data.first_air_date ?? data.release_date}
                                    </p>
                                </div>
                                { data.number_of_seasons &&
                                    <div className="flex flex-col items-center">
                                        <h4 className="font-semibold text-neutral-500 text-xs">Seasons</h4>
                                        <p className="text-sm text-neutral-400 ">
                                            {data.number_of_seasons}
                                        </p>
                                    </div>
                                }

                                 { data.runtime &&
                                    <div className="flex flex-col items-center">
                                        <h4 className="font-semibold text-neutral-500 text-xs">Runtime</h4>
                                        <p className="text-sm text-neutral-400 ">
                                            {data.runtime}m
                                        </p>
                                    </div>
                                }
                                 
                            </article>
                            {
                                data.genres &&
                                <article className="flex mt-5 gap-5 justify-center">
                                    <div className="flex flex-col items-center">
                                        <h4 className="font-semibold text-neutral-500 text-xs">Genres</h4>
                                        <p className="text-sm text-neutral-400 text-center">
                                            {data.genres.map((item:object,index:number) => index+1 == data.genres.length ? item.name + "" : item.name + ", ")}
                                        </p>
                                    </div> 
                                </article>
                            }

                            {
                                data.videos.length >= 1 &&
                                <article className="flex mt-5 gap-5 justify-center">
                                    <div className="flex flex-col items-center">
                                        <h4 className="font-semibold text-neutral-500 text-xs">Videos</h4>
                                        <div className="flex gap-2">
                                            {data.videos.sort((a,b) => { // sort so the first video is more representive of the media and not bts
                                                if((a.type =="Trailer" || a.type =="Teaser") && (b.type != "Trailer" || b.type !="Teaser")){return -1}
                                                else if((a.type != "Trailer" || a.type !="Teaser") && (b.type == "Trailer" || b.type == "Teaser")){return 1}
                                                else if((a.type == "Trailer" || a.type =="Teaser") && (b.type == "Trailer" || b.type == "Teaser")){return 0}
                                            }).map((item,index) => 
                                                <article className={`${index >= 5 ? "hidden":"flex flex-col"} items-center mt-2`}>
                                                    <img onClick={() => {activeVideo == item.key ? setActiveVideo("") : setActiveVideo(item.key)}} className={`h-7 hover:cursor-pointer ease-in-out duration-150 hover:scale-110 ${activeVideo == item.key ? "scale-110" : "scale-100"}`} src="https://bay-1.tor1.cdn.digitaloceanspaces.com/logos/youtube_social_icon_red.png"/>
                                                    {activeVideo == item.key ? <Dot className="ease-in-out duration-300" /> : null}
                                                </article>
                                            
                                            )} 
                                        </div>
                                    </div> 
                                </article>
                            }
                            

                            <article className="flex justify-center gap-5 mt-10">
                                {
                                    consumeLaterIdSet.has(id) ?
                                    <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Consume later</p>
                                        <Check 
                                        className={` hover:cursor-pointer`} />
                                    </div>
                                    :
                                     <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Consume later</p>
                                        <CalendarClockIcon onClick={() => handleConsumeLaterClick()}
                                        className={`${isConsumeLaterPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                    </div>
                                }
                               

                               {
                                    prevConsumedIdSet.has(id) ?
                                    <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                        <Check 
                                        className={` hover:cursor-pointer`} />
                                    </div>
                                    :
                                     <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                        <Clock onClick={() => handleConsumeLaterClick(true)}
                                        className={`${isConsumedPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                    </div>
                                }
                                
                                
                            </article>
                            
                             
                            

                            
                        </section>
                       
                    )}
                    </ScrollArea>
            </DialogContent>
        </Dialog>
        )
    }
    if(itemType == "songs"){
        return(
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="bg-black/60 backdrop-blur-md dark border-0 text-white max-h-full min-h-50">
                <ScrollArea className="flex max-h-svh pb-6 sm:pb-0">
                <DialogHeader className="pb-2">
                <DialogTitle>{isSuccess ? data.name : "Details"}</DialogTitle>
                <DialogDescription>
                    
                </DialogDescription>
                </DialogHeader>
                {isPending && <div className="flex items-center justify-center"><SecondaryLoader /></div>}
                    {isError && <p>Error loading details.</p>}
                    {isSuccess && data && (
                        <section className="flex relative flex-col w-full h-full overflow-hidden min-h-100">
                          <div className="flex relative w-full h-45 justify-center items-center overflow-hidden">
                            <Skeleton className="absolute inset-0 z-0 rounded-lg scale-100 h-[80%]" />
                            <iframe className="z-30 h-full" src={`https://open.spotify.com/embed/track/${id}?utm_source=generator`} width="100%" height="100%" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />
                          </div>
                            <article className="relative flex flex-col items-center">
                                <ItemDetails id={data.artists[0].id} itemType="artists">
                                    <div className="flex items-center justify-center gap-1 hover:cursor-pointer hover:underline">
                                        <p className="text-white text-sm hover:underline hover:cursor-pointer">
                                            {data.artists[0].name}
                                        </p>
                                        <ExternalLink className="h-4 w-4" />
                                    </div>
                                </ItemDetails>
                                <h3 className="font-semibold sm:text-lg">{data.name}</h3>
                                
                                <span className="flex items-center text-sm gap-1 font-semibold"><ChartLineIcon className="" /> {data.popularity/10} </span>
                            </article>
                            <article className="pt-5 text-xs text-neutral-500">
                               
                                
                            </article>

                            <article className="flex mt-5 gap-5 justify-center">
                                <div className="flex flex-col items-center">
                                    <h4 className="font-semibold text-neutral-500 text-xs text-center">Initial release</h4>
                                    <p className="text-sm text-neutral-400 ">
                                        {data.album.release_date}
                                    </p>
                                </div>
                                <div className="flex flex-col items-center">
                                    <h4 className="font-semibold text-neutral-500 text-xs">From</h4>
                                     <ItemDetails id={data.album.id} itemType="albums">
                                        <div className="flex items-center gap-1 hover:cursor-pointer hover:underline">
                                            <p className="text-sm text-white hover:cursor-pointer hover:underline max-w-50 text-wrap text-center">
                                                    {data.album.name}
                                            </p>
                                            <ExternalLink className="h-4 w-4" />
                                        </div>
                                    </ItemDetails>
                                </div>
                            </article>

                            <article className="flex justify-center gap-5 mt-10">
                                {
                                    consumeLaterIdSet.has(id) ?
                                    <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Consume later</p>
                                        <Check 
                                        className={` hover:cursor-pointer`} />
                                    </div>
                                    :
                                     <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Consume later</p>
                                        <CalendarClockIcon onClick={() => handleConsumeLaterClick()}
                                        className={`${isConsumeLaterPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                    </div>
                                }
                                {
                                    prevConsumedIdSet.has(id) ?
                                    <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                        <Check 
                                        className={` hover:cursor-pointer`} />
                                    </div>
                                    :
                                     <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                        <Clock onClick={() => handleConsumeLaterClick(true)}
                                        className={`${isConsumedPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                    </div>
                                }
                                
                                
                            </article>
                            
                             
                            

                            
                        </section>
                       
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
        )
    }
    if(itemType == "albums"){
        return(
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="bg-black/60 backdrop-blur-md dark border-0 text-white max-h-full min-h-50 ease-in-out duration-200">
                <ScrollArea className="flex max-h-svh pb-6 sm:pb-0">
                <DialogHeader className="pb-2">
                <DialogTitle>{isSuccess ? data.name : "Details"}</DialogTitle>
                <DialogDescription>
                    
                </DialogDescription>
                </DialogHeader>
                {isPending && <div className="flex items-center justify-center"><SecondaryLoader /></div>}
                    {isError && <p>Error loading details.</p>}
                    {isSuccess && data && (
                        <section className="flex relative flex-col w-full h-full gap-4 overflow-hidden min-h-100">
                          <div className="flex relative w-full h-45 justify-center items-center overflow-hidden">
                            <div
                                className="absolute inset-0 z-10 bg-center rounded-lg scale-100"
                                style={data.images[0].url ? {  backgroundImage: `url(${data.images[0].url})` } : {backgroundImage: "radial-gradient(circle,rgba(31, 38, 61, 1) 0%, rgba(10, 10, 28, 1) 48%, rgba(0, 0, 0, 1) 100%)"}}
                            />
                             <Skeleton className="absolute inset-0 z-0 rounded-lg scale-100 h-[80%]" />
                            <div className="flex absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
                          </div>

                            <header className="flex mb-4 justify-center items-center ">
                                <Button onClick={() => setActiveTab("overview")} className={`hover:cursor-pointer rounded-none bg-transparent hover:bg-transparent text-white
                                ${activeTab == "overview" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                    Overview
                                </Button>
                                <Button  onClick={() => setActiveTab("songs")} className={`hover:cursor-pointer rounded-none bg-transparent hover:bg-transparent text-white
                                ${activeTab == "songs" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                    Songs
                                </Button>
                            </header>
                            
                            { activeTab == "overview" &&

                                <>
                                <article className="relative flex flex-col items-center">
                                    <ItemDetails id={data.artists[0].id} itemType="artists">
                                        <div className="flex items-center justify-center gap-1 hover:cursor-pointer hover:underline">
                                        <p className="text-white text-sm hover:underline hover:cursor-pointer">
                                            {data.artists[0].name}
                                        </p>
                                        <ExternalLink className="h-4 w-4" />
                                    </div>
                                    </ItemDetails>
                                    <h3 className="font-semibold sm:text-lg">{data.name}</h3>
                                        <span className="flex items-center text-sm gap-1 font-semibold"><ChartLineIcon className="" /> {data.popularity/10} </span>
                                </article>

                                <article className="flex flex-col mt-2 gap-5 items-center justify-center">
                                    <div className="flex flex-col w-fit">
                                        <h4 className="font-semibold text-neutral-500 text-xs">Initial release</h4>
                                        <p className="text-sm text-neutral-400 ">
                                            {data.release_date}
                                        </p>
                                    </div>
                                    
                                </article>
                            
                                <div className="flex justify-center mt-2">
                                    <Button onClick={() => window.location = data.external_urls.spotify} className="bg-transparent hover:bg-transparent hover:cursor-pointer h-8">
                                        <img className="h-6" src="https://storage.googleapis.com/pr-newsroom-wp/1/2023/05/Spotify_Full_Logo_RGB_Green.png"/>
                                        </Button>
                                </div>

                                <article className="flex justify-center gap-5 mt-10">
                                    {
                                    consumeLaterIdSet.has(id) ?
                                        <div className="flex flex-col font-semibold items-center gap-1" >
                                            <p className="text-xs text-neutral-500 ">Consume later</p>
                                            <Check 
                                            className={` hover:cursor-pointer`} />
                                        </div>
                                        :
                                        <div className="flex flex-col font-semibold items-center gap-1" >
                                            <p className="text-xs text-neutral-500 ">Consume later</p>
                                            <CalendarClockIcon onClick={() => handleConsumeLaterClick()}
                                            className={`${isConsumeLaterPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                        </div>
                                    }
                                    {
                                    prevConsumedIdSet.has(id) ?
                                    <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                        <Check 
                                        className={` hover:cursor-pointer`} />
                                    </div>
                                    :
                                     <div className="flex flex-col font-semibold items-center gap-1" >
                                        <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                        <Clock onClick={() => handleConsumeLaterClick(true)}
                                        className={`${isConsumedPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                    </div>
                                }
                                    
                                </article>
                                </>
                            }
                            { activeTab == "songs" &&
                                <main className="flex flex-col w-full items-center gap-2">
                                    <h4 className="font-semibold text-neutral-400">{data.tracks.items.length} {data.tracks.items.length != 1 ? "Songs" : "Song"}</h4>
                                    <ScrollArea className="flex flex-col max-h-50 w-full md:w-3/4 items-center justify-center text-white z-20">
                                        {data.tracks.items.map((item,index) => 
                                            <ItemDetails itemType="songs" id={item.id}>
                                                <article key={item.id} className="flex w-full h-20 mb-2 hover:cursor-pointer rounded-lg hover:bg-black/40 ease-in-out duration-200">
                                                    <div className="flex relative px-2 items-center justify-center gap-2">
                                                        <img src={data.images[0].url ?? ""} className="h-16 rounded-lg z-10"/>
                                                        <Skeleton className="absolute h-16 w-16 left-2 rounded-lg z-0"/>
                                                        <h3 className="text-sm">{item.name}</h3>
                                                    </div>
                                                </article>
                                            </ItemDetails>
                                                
                                           
                                        )}
                                    </ScrollArea>

                                
                                </main>
                            }

                        </section>
                       
                    )}
            </ScrollArea>
            </DialogContent>
        </Dialog>
        )
    }
    if(itemType == "artists"){
        return(
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="bg-black/60 backdrop-blur-md dark border-0 text-white max-h-full min-h-50 ease-in-out duration-200">
                <ScrollArea className="flex max-h-svh pb-6 sm:pb-0">
                <DialogHeader className="pb-2">
                <DialogTitle>{isSuccess ? data.name : "Details"}</DialogTitle>
                <DialogDescription>
                    
                </DialogDescription>
                </DialogHeader>
                {isPending && <div className="flex items-center justify-center"><SecondaryLoader /></div>}
                    {isError && <p>Error loading details.</p>}
                    {isSuccess && data && (
                        <section className="flex relative flex-col w-full h-full overflow-hidden min-h-100">
                          <div className="flex relative w-full h-55 justify-center items-center overflow-hidden">
                            <div
                                className="absolute inset-0 bg-center rounded-lg scale-100"
                                style={data.images[0] ? {  backgroundImage: `url(${data.images[0].url})` } : {backgroundImage: "radial-gradient(circle,rgba(31, 38, 61, 1) 0%, rgba(10, 10, 28, 1) 48%, rgba(0, 0, 0, 1) 100%)"}}
                            />
                         
                            <div className="flex absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
                          </div>

                        <header className="flex mb-4 justify-center pt-2">

                            
                            <Button onClick={() => setActiveTab("overview")} className={`hover:cursor-pointer rounded-none bg-transparent hover:bg-transparent text-white
                            ${activeTab == "overview" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                Overview
                            </Button>
                            <Button  onClick={() => setActiveTab("albums")} className={`hover:cursor-pointer rounded-none bg-transparent hover:bg-transparent text-white
                            ${activeTab == "albums" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                Albums
                            </Button>
                        </header>
                        
                        {
                            activeTab == "overview" &&
                            <main className="flex flex-col w-full items-center gap-2">
                            <article className="relative just flex flex-col items-center justify-center">
                            <h3 className="font-semibold sm:text-lg">{data.name}</h3>
                                <span className="flex items-center text-sm gap-1 font-semibold"><ChartLineIcon className="h-4 w-4" /> {data.popularity/10} </span>
                            </article>

                            <article className="flex mt-2 items-center justify-center gap-4">
                                <div className="flex flex-col items-center w-fit">
                                    <p className="flex items-center text-sm font-semibold text-neutral-400 ">
                                        Spotify followers
                                    </p>
                                    <p className="text-sm text-neutral-400 ">
                                        {data.followers.total ?? ""}
                                    </p>
                                </div>
                                { data.genres.length >= 1 &&
                                    <div className="flex flex-col items-center text-center w-fit">
                                        <p className="flex items-center text-sm font-semibold text-neutral-400 ">
                                            Genres
                                        </p>
                                        <p className="flex text-sm text-neutral-400 max-w-35 md:max-w-50 justify-center">
                                            {data.genres.map((item:string,index:number) => index+1 == data.genres.length ? item + "" : item + ", ")}
                                        </p>
                                    </div>
                                }     
                            </article>
                            <div className="flex justify-center mt-2">
                                <Button onClick={() => window.location = data.external_urls.spotify} className="bg-transparent hover:bg-transparent hover:cursor-pointer h-8">
                                    <img className="h-6" src="https://storage.googleapis.com/pr-newsroom-wp/1/2023/05/Spotify_Full_Logo_RGB_Green.png"/>
                                </Button>
                            </div>

                            </main>
                        }
                        {
                            activeTab == "albums" &&
                            <main className="flex flex-col w-full items-center gap-2">
                                <h4 className="font-semibold text-neutral-400">{data.albums.length} {data.albums.length != 1 ? "Albums" : "Album"}</h4>
                                <ScrollArea className="flex flex-col max-h-50 w-3/4 px-5 text-white">
                                    {data.albums.map((item:any, index:number) => 
                                        <ItemDetails id={item.id} itemType="albums">
                                            <article key={item.id} className="flex w-full h-20 mb-2 hover:cursor-pointer">
                                                <img src={item.images[0].url} />
                                                <div className="flex flex-col ml-4 items-center justify-center">
                                                    <h5 className="text-xs font-semibold">{item.name}</h5>
                                                </div>
                                            </article>
                                        </ItemDetails>
                                    )}
                                </ScrollArea>

                            
                            </main>
                        }
                        
                        
                            

                        <article className="flex justify-center gap-5 mt-10 items-end">
                
                            {
                            consumeLaterIdSet.has(id) ?
                                <div className="flex flex-col font-semibold items-center gap-1" >
                                    <p className="text-xs text-neutral-500 ">Consume later</p>
                                    <Check 
                                    className={` hover:cursor-pointer`} />
                                </div>
                                :
                                <div className="flex flex-col font-semibold items-center gap-1" >
                                    <p className="text-xs text-neutral-500 ">Consume later</p>
                                    <CalendarClockIcon onClick={() => handleConsumeLaterClick()}
                                    className={`${isConsumeLaterPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                </div>
                            }
                            
                            {
                                prevConsumedIdSet.has(id) ?
                                <div className="flex flex-col font-semibold items-center gap-1" >
                                    <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                    <Check 
                                    className={`hover:cursor-pointer`} />
                                </div>
                                :
                                    <div className="flex flex-col font-semibold items-center gap-1" >
                                    <p className="text-xs text-neutral-500 ">Recently consumed</p>
                                    <Clock onClick={() => handleConsumeLaterClick(true)}
                                    className={`${isConsumedPending ? "cursor-not-allowed opacity-50":""} hover:cursor-pointer`} />
                                </div>
                            }
                        </article>
                            
                        </section>
                       
                    )}
            </ScrollArea>
            </DialogContent>
        </Dialog>
        )
    }

    else{
        return(children)
    }


    
}